import { Directive, effect, inject, input, TemplateRef, ViewContainerRef } from '@angular/core';
import { type ProjectRole } from '../../../feature-seatmap/constants/roles.constants';
import { ProjectFacadeStrategy } from '../../../feature-seatmap';

/**
 * Structural directive that conditionally renders content based on the current user's project role.
 *
 * Requires `ProjectFacade` to be provided in the component tree (via `provideProjectDomain()`).
 *
 * Usage (admin-only):
 *   <button hlmBtn *ocpHasProjectRole="Roles.Admin" type="button">Admin action</button>
 *
 * Usage (admin OR editor):
 *   <button hlmBtn *ocpHasProjectRole="[Roles.Admin, Roles.Editor]" type="button">
 *     Shared action
 *   </button>
 */
@Directive({
  selector: '[ocpHasProjectRole]',
})
export class HasProjectRoleDirective {
  private readonly vcr = inject(ViewContainerRef);
  private readonly tmpl = inject(TemplateRef<unknown>);
  private readonly projectFacade = inject(ProjectFacadeStrategy);

  readonly ocpHasProjectRole = input.required<ProjectRole | ProjectRole[]>();

  constructor() {
    effect(() => {
      const value = this.ocpHasProjectRole();
      const roles: ProjectRole[] = Array.isArray(value) ? value : [value];

      const hasRole = roles.some(r =>
        r === 'admin' ? this.projectFacade.isProjectAdmin() : this.projectFacade.isProjectEditor()
      );

      this.vcr.clear();
      if (hasRole) {
        this.vcr.createEmbeddedView(this.tmpl);
      }
    });
  }
}
